package org.zq.astrock_ai.ui.research.gushi.data

/** 最近两次扫描快照的差异摘要。 */
data class ScanHistoryDiff(
    val newerAtMs: Long,
    val olderAtMs: Long,
    val appearedBuy: List<String>,
    val appearedSell: List<String>,
    val flippedToBuy: List<String>,
    val flippedToSell: List<String>,
    val disappeared: List<String>,
) {
    val totalChanges: Int
        get() = appearedBuy.size + appearedSell.size + flippedToBuy.size +
            flippedToSell.size + disappeared.size
}

fun diffScanHistorySnapshots(
    newer: ScanHistorySnapshot,
    older: ScanHistorySnapshot,
): ScanHistoryDiff {
    fun signalMap(s: ScanHistorySnapshot): Map<String, String> {
        if (s.highlights.isNotEmpty()) {
            return s.highlights
                .associate { it.code to it.signal.uppercase() }
                .filterValues { it == "BUY" || it == "SELL" }
        }
        return s.changes.mapNotNull { c ->
            val sig = when (c.kind) {
                "NEW_BUY", "TO_BUY" -> "BUY"
                "NEW_SELL", "TO_SELL" -> "SELL"
                else -> null
            } ?: return@mapNotNull null
            c.code to sig
        }.toMap()
    }

    val a = signalMap(newer)
    val b = signalMap(older)
    val appearedBuy = mutableListOf<String>()
    val appearedSell = mutableListOf<String>()
    val flippedToBuy = mutableListOf<String>()
    val flippedToSell = mutableListOf<String>()
    val disappeared = mutableListOf<String>()

    a.forEach { (code, sig) ->
        val prev = b[code]
        when {
            prev == null && sig == "BUY" -> appearedBuy += code
            prev == null && sig == "SELL" -> appearedSell += code
            prev != null && prev != sig && sig == "BUY" -> flippedToBuy += code
            prev != null && prev != sig && sig == "SELL" -> flippedToSell += code
        }
    }
    b.forEach { (code, sig) ->
        if (code !in a && (sig == "BUY" || sig == "SELL")) {
            disappeared += code
        }
    }

    return ScanHistoryDiff(
        newerAtMs = newer.atMs,
        olderAtMs = older.atMs,
        appearedBuy = appearedBuy,
        appearedSell = appearedSell,
        flippedToBuy = flippedToBuy,
        flippedToSell = flippedToSell,
        disappeared = disappeared,
    )
}

/** 相邻快照链式对比（entries[0] 最新）。默认最多 4 段。 */
fun chainScanHistoryDiffs(
    entries: List<ScanHistorySnapshot>,
    maxPairs: Int = 4,
): List<ScanHistoryDiff> {
    if (entries.size < 2 || maxPairs <= 0) return emptyList()
    val pairs = (entries.size - 1).coerceAtMost(maxPairs)
    return (0 until pairs).map { i ->
        diffScanHistorySnapshots(entries[i], entries[i + 1])
    }
}

fun exportScanDiffText(diffs: List<ScanHistoryDiff>): String {
    if (diffs.isEmpty()) return "暂无差异可导出"
    return buildString {
        appendLine("扫描差异链导出")
        diffs.forEachIndexed { index, d ->
            appendLine()
            appendLine(
                "#${index + 1} ${formatScanHistoryTime(d.newerAtMs)} ← ${formatScanHistoryTime(d.olderAtMs)} · 共${d.totalChanges}项",
            )
            fun line(label: String, codes: List<String>) {
                if (codes.isEmpty()) return
                appendLine("$label: ${codes.joinToString(",")}")
            }
            line("新现买", d.appearedBuy)
            line("新现卖", d.appearedSell)
            line("转买", d.flippedToBuy)
            line("转卖", d.flippedToSell)
            line("信号消失", d.disappeared)
            if (d.totalChanges == 0) appendLine("(无变化)")
        }
    }
}
